import React, { useState, useEffect } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import {
  CheckCircle,
} from '@mui/icons-material';
import SvgIcon from '@mui/material/SvgIcon';
import { ReactComponent as SidebarFeedbackIcon } from '../assets/icons/sidebar_feedback.svg';
import PageHeader from '../components/PageHeader';
import { feedbackApi, storeApi } from '../services/api';
import { useAuth } from '../auth/AuthContext';
import { useNavigate } from 'react-router-dom';

const inputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#fff',
    borderRadius: '10px',
    '& fieldset': { borderColor: '#e7e7e7' },
    '&:hover fieldset': { borderColor: '#c4c4c4' },
    '&.Mui-focused fieldset': { borderColor: '#0071e3' },
  },
};

const labelSx = {
  fontSize: '0.875rem',
  fontWeight: 600,
  color: '#3c3c3c',
  mb: 0.75,
  display: 'block',
};

const readOnlyInputSx = {
  '& .MuiOutlinedInput-root': {
    bgcolor: '#f0f0f0',
    borderRadius: '10px',
    '& fieldset': { borderColor: '#e7e7e7' },
  },
};

const FeedbackPage: React.FC = () => {
  const { storeId } = useAuth();
  const navigate = useNavigate();

  const [inquiryType, setInquiryType] = useState('request');
  const [companyName, setCompanyName] = useState('');
  const [storeName, setStoreName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');

  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    const fetchStoreInfo = async () => {
      if (!storeId) return;
      try {
        const storeInfo = await storeApi.getStoreInfo(storeId);
        setStoreName(storeInfo.name || '');
        setCompanyName(storeInfo.company_name || '');
      } catch {
        // 取得失敗時は空のまま
      }
    };
    fetchStoreInfo();
  }, [storeId]);

  const scrollToField = (fieldName: string) => {
    setTimeout(() => {
      const el = document.querySelector(`[name="${fieldName}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (el as HTMLElement).focus();
      }
    }, 100);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage(null);

    const errors: Record<string, string> = {};
    if (!companyName) errors.companyName = '会社名を入力してください';
    if (!email) errors.email = 'メールアドレスを入力してください';
    if (!message) errors.message = '内容詳細を入力してください';

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      scrollToField(Object.keys(errors)[0]);
      return;
    }
    setFieldErrors({});

    setIsSubmitting(true);
    try {
      const result = await feedbackApi.submit({
        inquiry_type: inquiryType,
        company_name: companyName,
        store_name: storeName,
        email,
        message,
      });
      setShowSuccessDialog(true);
      setInquiryType('request');
      setEmail('');
      setMessage('');
    } catch {
      setErrorMessage('送信に失敗しました。しばらく経ってからもう一度お試しください。');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f8f9' }}>
      <PageHeader
        title="機能に関するご意見・ご要望"
        icon={<SvgIcon component={SidebarFeedbackIcon} inheritViewBox sx={{ color: '#898989', fontSize: 22 }} />}
        description={
          <>
            <Typography sx={{ fontSize: '11px', color: '#3c3c3c' }}>
              管理画面の機能についてのご意見・ご要望を送信できます。
            </Typography>
            <Typography sx={{ fontSize: '11px', color: '#3c3c3c' }}>
              いただいた内容は管理画面の改修に活用します。
            </Typography>
          </>
        }
      />

      {/* フォーム */}
      <Box component="form" onSubmit={handleSubmit} sx={{ px: 2.5, pb: 4 }}>
        {errorMessage && (
          <Alert severity="error" onClose={() => setErrorMessage(null)} sx={{ mb: 3, borderRadius: 2 }}>
            {errorMessage}
          </Alert>
        )}

        {/* お問い合わせ内容 */}
        <Box sx={{ mb: 1 }}>
          <Typography component="label" sx={labelSx}>お問い合わせ内容</Typography>
            <RadioGroup
            row
            value={inquiryType}
            onChange={(e) => setInquiryType(e.target.value)}
            >
            <FormControlLabel
              value="request"
              control={<Radio sx={{ color: '#c4c4c4', '& .MuiSvgIcon-root': { color: 'inherit' }, '&.Mui-checked': { color: '#0071e3' } }} />}
              label={<Typography sx={{ fontSize: '18px', color: '#3c3c3c', fontWeight: 'bold' }}>ご要望</Typography>}
            />
            <FormControlLabel
              value="question"
              control={<Radio sx={{ color: '#c4c4c4', '& .MuiSvgIcon-root': { color: 'inherit' }, '&.Mui-checked': { color: '#0071e3' } }} />}
              label={<Typography sx={{ fontSize: '18px', color: '#3c3c3c', fontWeight: 'bold' }}>ご質問</Typography>}
            />
            </RadioGroup>
        </Box>

        {/* 会社名 */}
        <Box sx={{ mb: 2 }}>
          <Typography component="label" sx={labelSx}>会社名</Typography>
          <TextField
            name="companyName"
            value={companyName}
            onChange={(e) => { setCompanyName(e.target.value); setFieldErrors((prev) => { const { companyName: _, ...rest } = prev; return rest; }); }}
            fullWidth
            error={!!fieldErrors.companyName}
            helperText={fieldErrors.companyName}
            sx={inputSx}
          />
        </Box>

        {/* 店舗名 */}
        <Box sx={{ mb: 2 }}>
          <Typography component="label" sx={labelSx}>店舗名</Typography>
          <TextField
            value={storeName}
            fullWidth
            InputProps={{ readOnly: true }}
            sx={readOnlyInputSx}
          />
        </Box>

        {/* メールアドレス */}
        <Box sx={{ mb: 2 }}>
          <Typography component="label" sx={labelSx}>メールアドレス</Typography>
          <TextField
            name="email"
            value={email}
            onChange={(e) => { setEmail(e.target.value); setFieldErrors((prev) => { const { email: _, ...rest } = prev; return rest; }); }}
            fullWidth
            type="email"
            placeholder="example@example.com"
            error={!!fieldErrors.email}
            helperText={fieldErrors.email}
            sx={inputSx}
          />
        </Box>

        {/* 内容詳細 */}
        <Box sx={{ mb: 2 }}>
          <Typography component="label" sx={labelSx}>内容詳細（最大1,000文字）</Typography>
          <TextField
            name="message"
            value={message}
            onChange={(e) => { setMessage(e.target.value); setFieldErrors((prev) => { const { message: _, ...rest } = prev; return rest; }); }}
            fullWidth
            multiline
            rows={5}
            placeholder="お問い合わせの詳細をお書きください"
            inputProps={{ maxLength: 1000 }}
            error={!!fieldErrors.message}
            helperText={fieldErrors.message}
            sx={inputSx}
          />
        </Box>

        <Button
          type="submit"
          variant="contained"
          fullWidth
          disabled={isSubmitting}
          sx={{
            bgcolor: '#0071e3',
            '&:hover': { bgcolor: '#0062c4' },
            borderRadius: 2,
            py: 1.25,
            fontWeight: 600,
            fontSize: '15px',
          }}
        >
          {isSubmitting ? '送信中...' : 'この内容で送信する'}
        </Button>
      </Box>

      {/* 送信完了ダイアログ */}
      <Dialog
        open={showSuccessDialog}
        PaperProps={{
          sx: {
            borderRadius: 3,
            px: 3,
            py: 4,
            textAlign: 'center',
            maxWidth: 340,
          },
        }}
      >
        <DialogContent sx={{ p: 0 }}>
          <CheckCircle sx={{ fontSize: 56, color: '#4caf50', mb: 2 }} />
          <Typography sx={{ fontSize: '18px', fontWeight: 600, color: '#3c3c3c', mb: 3 }}>
            送信完了しました
          </Typography>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate('/')}
            sx={{
              bgcolor: '#0071e3',
              '&:hover': { bgcolor: '#0062c4' },
              borderRadius: 2,
              py: 1.25,
              fontWeight: 600,
              fontSize: '15px',
            }}
          >
            ホームに戻る
          </Button>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default FeedbackPage;
